import React, { useRef, useState } from "react";
import styled from "styled-components";
import CustomImage from "../../../common/components/image/CustomImage";
import CustomIcon from "../../../common/components/image/CustomIcon";
import { useClickOutside } from "../../hooks/useClickOutside";
import DeviceMenu from "../../../pages/store/components/DeviceMenu";
import DeviceHoverCard from "../../../pages/store/components/DeviceHoverCard";
import { Theme } from "../../Theme";

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    font-family: "Pretendard", sans-serif;
    background-color: ${Theme.color.white};
    //border: 1px solid #e6e6e9;
    border-radius: 16px;
    cursor: pointer;
`;

const StyledTable = styled.table`
    border-collapse: collapse;
    width: 100%;
`;

const StyledTbody = styled.tbody``;

const StyledTableRow = styled.tr``;

const StyledDataCell = styled.td`
    padding: 0;
    vertical-align: top;
    //border-right: 1px solid #e6e6e9; // #6250fc
    &:last-child {
        border-right: none;
    }
    &:hover {
        background-color: ${Theme.color.indigo500_007};
    }
`;

const InnerCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${({ alignItem }) => alignItem ?? "center"};
    justify-content: ${({ justifyContent }) => justifyContent};
    min-width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    background-color: transparent;
    box-sizing: border-box;
    border-bottom: ${({ borderBottom, $isRowHover }) =>
        borderBottom ? `1px solid ${$isRowHover ? Theme.color.indigo500 : Theme.color.gray200}` : "none"}; // #6250fc
    border-right: ${({ $isColHover }) =>
        $isColHover ? `1px solid ${Theme.color.indigo500}` : `1px solid ${Theme.color.gray200}`};
    border-top: ${({ borderTop, $isTopHover }) =>
        borderTop ? `1px solid ${$isTopHover ? Theme.color.indigo500 : Theme.color.gray200}` : "none"};
    border-left: ${({ borderLeft, $isLeftHover }) =>
        borderLeft ? `1px solid ${$isLeftHover ? Theme.color.indigo500 : Theme.color.gray200}` : "none"};
    border-bottom-left-radius: ${({ $isBottomLeft }) => ($isBottomLeft ? "16px" : "0")};
    border-bottom-right-radius: ${({ $isBottomRight }) => ($isBottomRight ? "16px" : "0")};
    border-top-left-radius: ${({ $isTopLeft }) => ($isTopLeft ? "16px" : "0")};
    border-top-right-radius: ${({ $isTopRight }) => ($isTopRight ? "16px" : "0")};
`;

const EntranceCellContainer = styled.div`
    display: flex;
    flex-direction: column; /* 기본: 세로 3분할 (t/b) */
    min-width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    //border-bottom: 1px solid #e6e6e9; /* 셀 간 구분선 */
    //border-right: 1px solid #e6e6e9;
    border-bottom: ${({ $isBottomHover }) =>
        $isBottomHover ? `1px solid ${Theme.color.indigo500}` : `1px solid ${Theme.color.gray200}`}; // #6250fc
    border-right: ${({ $isRightHover }) =>
        $isRightHover ? `1px solid ${Theme.color.indigo500}` : `1px solid ${Theme.color.gray200}`};
    border-top: ${({ borderTop, $isTopHover }) =>
        borderTop ? `1px solid ${$isTopHover ? Theme.color.indigo500 : Theme.color.gray200}` : "none"};
    border-left: ${({ borderLeft, $isLeftHover }) =>
        borderLeft ? `1px solid ${$isLeftHover ? Theme.color.indigo500 : Theme.color.gray200}` : "none"};
    border-bottom-left-radius: ${({ $isBottomLeft }) => ($isBottomLeft ? "16px" : "0")};
    border-bottom-right-radius: ${({ $isBottomRight }) => ($isBottomRight ? "16px" : "0")};
    border-top-left-radius: ${({ $isTopLeft }) => ($isTopLeft ? "16px" : "0")};
    border-top-right-radius: ${({ $isTopRight }) => ($isTopRight ? "16px" : "0")};
`;

const EntranceRowContainer = styled.div`
    display: flex;
    flex-direction: row; /* 가로 3분할 (l/r)을 위한 Row */
    width: 100%;
    height: 100%;
`;

const EntranceSegment = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent; /* 빈 셀 배경 투명 */

    /* 세로 3분할 (t/b) 스타일 */
    ${({ layoutType }) =>
        (layoutType === "t" || layoutType === "b") &&
        `
        height: 33.33%;
        width: 100%;
    `}

    /* 가로 3분할 (l/r) 스타일 */
    ${({ layoutType }) =>
        (layoutType === "l" || layoutType === "r") &&
        `
        width: 33.33%;
        height: 100%;
    `}
`;

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const CellContent = ({
    item,
    openHistoryModal,
    openDiagnosisModal,
    openMemoModal,
    openSettingModal,
    setOpenHistoryModal,
    setOpenDiagnosisModal,
    setOpenMemoModal,
    setOpenSettingModal,
    isOpenedAnyDeviceMenu,
    setIsOpenedAnyDeviceMenu,
}) => {
    const [openedMenu, setIsOpenedMenu] = useState(false);
    const [openedTooltip, setOpenedTooltip] = useState(undefined);
    const menuRef = useRef(null);

    const toggleMenu = (device) => {
        setIsOpenedMenu((prev) => {
            if (prev !== undefined) {
                setIsOpenedAnyDeviceMenu(false);
                return undefined;
            } else {
                setTimeout(() => setIsOpenedAnyDeviceMenu(true), 200);
                setIsOpenedAnyDeviceMenu(true);
                return device.id;
            }
        });
    };

    useClickOutside(menuRef, () => {
        setTimeout(() => {
            if (
                openedMenu &&
                openHistoryModal?.id !== openedMenu &&
                openDiagnosisModal?.id !== openedMenu &&
                openMemoModal?.id !== openedMenu &&
                openSettingModal?.id !== openedMenu
            ) {
                setIsOpenedMenu(undefined);
                setIsOpenedAnyDeviceMenu(false);
            }
        }, 250);
    });

    return (
        <MenuContainer ref={menuRef}>
            <CustomImage
                imageName={`image_${item.device.type}`}
                width={"34px"}
                height={"42px"}
                cursor={"pointer"}
                onClick={() => toggleMenu(item.device)}
                onMouseEnter={() => setOpenedTooltip(item.device.id)}
                onMouseLeave={() => setOpenedTooltip(undefined)}
            />
            {item.device.id === openedMenu && (
                <DeviceMenu
                    device={item.device}
                    onHistoryClick={() => setOpenHistoryModal(item.device)}
                    onDiagnosisClick={() => setOpenDiagnosisModal(item.device)}
                    onMemoClick={() => setOpenMemoModal(item.device)}
                    onSettingClick={() => setOpenSettingModal(item.device)}
                />
            )}
            {item.device.id === openedTooltip && !isOpenedAnyDeviceMenu && (
                <DeviceHoverCard device={item.device} onMouseEnter={() => setOpenedTooltip(item.device.id)} />
            )}
        </MenuContainer>
    );
};

const EditLayerTable = ({ column = 4, row = 1, data, entranceLayout, cellWidth = 232, cellHeight = 132 }) => {
    const innerCellWidth = cellWidth / 2;
    const innerCellHeight = cellHeight / 2;

    const rowsArray = Array.from({ length: row });
    const columnsArray = Array.from({ length: column });

    const locationMap = data.reduce((map, item) => {
        // prop으로 받은 row와 column은 1부터 시작한다고 가정하고, 배열 인덱스(0부터)로 변환
        const rIndex = item.location.row - 1;
        const cIndex = item.location.column - 1;
        const type = item.location.bottom ? "b" : "t";

        const key = `r${rIndex}c${cIndex}${type}`;

        if (rIndex >= 0 && rIndex < row && cIndex >= 0 && cIndex < column) {
            map[key] = item;
        }
        return map;
    }, {});

    const [isOpenedAnyDeviceMenu, setIsOpenedAnyDeviceMenu] = useState(false);

    // history modal
    const [openHistoryModal, setOpenHistoryModal] = useState(undefined);

    // diagnosis modal
    const [openDiagnosisModal, setOpenDiagnosisModal] = useState(undefined);

    // memo modal
    const [openMemoModal, setOpenMemoModal] = useState(undefined);

    // setting modal
    const [openSettingModal, setOpenSettingModal] = useState(undefined);

    const [hoverCell, setHoverCell] = useState(undefined);

    const menuRef = useRef(null);

    const renderCellContent = (rowIndex, colIndex, isBottom) => {
        const type = isBottom ? "b" : "t";
        const key = `r${rowIndex}c${colIndex}${type}`;
        const item = locationMap[key];

        if (item) {
            return (
                <CellContent
                    item={item}
                    openDiagnosisModal={openDiagnosisModal}
                    openHistoryModal={openHistoryModal}
                    openMemoModal={openMemoModal}
                    openSettingModal={openSettingModal}
                    setOpenDiagnosisModal={setOpenDiagnosisModal}
                    setOpenHistoryModal={setOpenHistoryModal}
                    setOpenMemoModal={setOpenMemoModal}
                    setOpenSettingModal={setOpenSettingModal}
                    isOpenedAnyDeviceMenu={isOpenedAnyDeviceMenu}
                    setIsOpenedAnyDeviceMenu={setIsOpenedAnyDeviceMenu}
                />
            );
        }
        return null;
    };

    /**
     * entranceLayout 조건에 따라 세분화된 셀을 렌더링하는 함수
     */
    const renderEntranceLayout = (type, colIndex, rowIndex) => {
        // 't', 'b', 'l', 'r' 중 어디에 그림을 렌더링할지 결정
        const targetIndex = type === "t" || type === "l" ? 0 : 2; // t/l: 첫 번째(인덱스 0), b/r: 세 번째(인덱스 2)
        let rotate = 0;
        if (type === "b") rotate = 180;
        else if (type === "r") rotate = 90;
        else if (type === "l") rotate = -90;

        const content = <CustomIcon iconName={`arrow`} size={"24px"} rotate={rotate} />; // 예시 그림

        // 세그먼트 배열 생성 (3분할)
        const segments = Array.from({ length: 3 }, (_, index) => (
            <EntranceSegment key={index} layoutType={type}>
                {index === targetIndex ? content : null}
            </EntranceSegment>
        ));

        // 가로 3분할 (l/r)인 경우, 세로(column)를 가로(row) 컨테이너로 감쌈
        if (type === "l" || type === "r") {
            return (
                <EntranceCellContainer
                    width={innerCellWidth}
                    height={cellHeight}
                    onMouseEnter={() => setHoverCell({ row: rowIndex, col: colIndex })}
                    onMouseLeave={() => setHoverCell(undefined)}
                    $isBottomHover={
                        (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                        (hoverCell?.row !== 0 && hoverCell?.row === rowIndex + 1 && hoverCell?.col === colIndex)
                    }
                    borderTop={rowIndex === 0}
                    $isTopHover={hoverCell?.row === rowIndex && hoverCell?.col === colIndex}
                    $isRightHover={
                        (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                        (hoverCell?.col !== 0 && hoverCell?.row === rowIndex && hoverCell?.col === colIndex + 1)
                    }
                    borderLeft={colIndex === 0}
                    $isLeftHover={hoverCell?.col === 0 && hoverCell?.row === rowIndex && hoverCell?.col === colIndex}
                    $isTopLeft={colIndex === 0 && rowIndex === 0}
                    $isTopRight={colIndex === column - 1 && rowIndex === 0}
                    $isBottomLeft={colIndex === 0 && rowIndex === row - 1}
                    $isBottomRight={colIndex === column - 1 && rowIndex === row - 1}
                >
                    <EntranceRowContainer>{segments}</EntranceRowContainer>
                </EntranceCellContainer>
            );
        }

        // 세로 3분할 (t/b)인 경우, 세로(column) 컨테이너에 segments를 바로 렌더링
        return (
            <EntranceCellContainer
                width={innerCellWidth}
                height={cellHeight}
                onMouseEnter={() => setHoverCell({ row: rowIndex, col: colIndex })}
                onMouseLeave={() => setHoverCell(undefined)}
                $isBottomHover={
                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                    (hoverCell?.row !== 0 && hoverCell?.row === rowIndex + 1 && hoverCell?.col === colIndex)
                }
                borderTop={rowIndex === 0}
                $isTopHover={hoverCell?.row === rowIndex && hoverCell?.col === colIndex}
                $isRightHover={
                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                    (hoverCell?.col !== 0 && hoverCell?.row === rowIndex && hoverCell?.col === colIndex + 1)
                }
                borderLeft={colIndex === 0}
                $isLeftHover={hoverCell?.col === 0 && hoverCell?.row === rowIndex && hoverCell?.col === colIndex}
                $isTopLeft={colIndex === 0 && rowIndex === 0}
                $isTopRight={colIndex === column - 1 && rowIndex === 0}
                $isBottomLeft={colIndex === 0 && rowIndex === row - 1}
                $isBottomRight={colIndex === column - 1 && rowIndex === row - 1}
            >
                {segments}
            </EntranceCellContainer>
        );
    };

    return (
        <TableContainer>
            <StyledTable>
                <StyledTbody>
                    {rowsArray.map((_, rowIndex) => (
                        <StyledTableRow key={rowIndex}>
                            {columnsArray.map((_, colIndex) => {
                                const isEntranceCell =
                                    entranceLayout &&
                                    entranceLayout.row - 1 === rowIndex &&
                                    entranceLayout.column - 1 === colIndex;

                                if (isEntranceCell) {
                                    // entranceLayout이 적용되는 셀
                                    return (
                                        <StyledDataCell key={colIndex}>
                                            {renderEntranceLayout(entranceLayout.type, colIndex, rowIndex)}
                                        </StyledDataCell>
                                    );
                                } else {
                                    // 일반적인 상/하 2분할 셀
                                    return (
                                        <StyledDataCell
                                            key={colIndex}
                                            ref={menuRef}
                                            onMouseEnter={() => setHoverCell({ row: rowIndex, col: colIndex })}
                                            onMouseLeave={() => setHoverCell(undefined)}
                                        >
                                            <InnerCell
                                                width={innerCellWidth}
                                                height={innerCellHeight}
                                                justifyContent={"flex-end"}
                                                borderTop={rowIndex === 0}
                                                borderLeft={colIndex === 0}
                                                $isColHover={
                                                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                                                    (hoverCell?.col !== 0 &&
                                                        hoverCell?.row === rowIndex &&
                                                        hoverCell?.col === colIndex + 1)
                                                }
                                                $isRowHover={
                                                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                                                    (hoverCell?.row !== 0 &&
                                                        hoverCell?.row === rowIndex + 1 &&
                                                        hoverCell?.col === colIndex)
                                                }
                                                $isTopHover={
                                                    hoverCell?.row === 0 &&
                                                    hoverCell?.row === rowIndex &&
                                                    hoverCell?.col === colIndex
                                                }
                                                $isLeftHover={
                                                    hoverCell?.col === 0 &&
                                                    hoverCell?.row === rowIndex &&
                                                    hoverCell?.col === colIndex
                                                }
                                                $isTopLeft={colIndex === 0 && rowIndex === 0}
                                                $isTopRight={colIndex === column - 1 && rowIndex === 0}
                                            >
                                                {renderCellContent(rowIndex, colIndex, false)}
                                            </InnerCell>
                                            <InnerCell
                                                width={innerCellWidth}
                                                height={innerCellHeight}
                                                justifyContent={"flex-start"}
                                                borderBottom={true}
                                                borderLeft={colIndex === 0}
                                                $isColHover={
                                                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                                                    (hoverCell?.col !== 0 &&
                                                        hoverCell?.row === rowIndex &&
                                                        hoverCell?.col === colIndex + 1)
                                                }
                                                $isRowHover={
                                                    (hoverCell?.row === rowIndex && hoverCell?.col === colIndex) ||
                                                    (hoverCell?.row !== 0 &&
                                                        hoverCell?.row === rowIndex + 1 &&
                                                        hoverCell?.col === colIndex)
                                                }
                                                $isLeftHover={
                                                    hoverCell?.col === 0 &&
                                                    hoverCell?.row === rowIndex &&
                                                    hoverCell?.col === colIndex
                                                }
                                                $isBottomLeft={colIndex === 0 && rowIndex === row - 1}
                                                $isBottomRight={colIndex === column - 1 && rowIndex === row - 1}
                                            >
                                                {renderCellContent(rowIndex, colIndex, true)}
                                            </InnerCell>
                                        </StyledDataCell>
                                    );
                                }
                            })}
                        </StyledTableRow>
                    ))}
                </StyledTbody>
            </StyledTable>
        </TableContainer>
    );
};

export default EditLayerTable;
